import type { ConversationAssistantConfig } from '@atlassian/conversation-assistant-widget/types';
import { getDocument } from '@atlaskit/browser-apis';

import { IS_SYNTHETIC_TRAFFIC_QUERY_PARAM } from '../constants';

/**
 * Check if the synthetic traffic flag is set in the URL.
 */
const isSyntheticTraffic = () => {
	return (
		new URLSearchParams(window.location.search).get(IS_SYNTHETIC_TRAFFIC_QUERY_PARAM) === 'true'
	);
};

export const getSiteId = (): string => {
	return (
		new URLSearchParams(window.location.search).get('cloudId') ??
		new URLSearchParams(window.location.search).get('siteId') ??
		'85b4c2de-d8b1-4446-99ce-6eb9648ec652'
	);
};

const getWidgetId = () => {
	return new URLSearchParams(window.location.search).get('widgetId');
};

export const getConfig = (): ConversationAssistantConfig => {
	return {
		siteId: getSiteId(),
		widgetId: getWidgetId(),
		product: 'csm',
		assistanceServiceConfig: {
			headers: {
				'x-cloudid': getSiteId(),
				...(isSyntheticTraffic()
					? {
							'x-issynthetic-traffic': 'true',
						}
					: {}),
			},
		},
	};
};

export const createChatStatusPromise = (): {
	notifyChatIsOnline: () => void;
	waitForChatToBeOnline: Promise<void>;
} => {
	const resolveRef = {
		resolve: () => {},
	};

	const waitForChatToBeOnline = new Promise<void>((resolve) => {
		resolveRef.resolve = resolve;
	});

	const notifyChatIsOnline = (): void => {
		resolveRef.resolve();
	};

	return {
		notifyChatIsOnline,
		waitForChatToBeOnline,
	};
};

export const getAccountIdFromMeta = (): string | null => {
	const meta = getDocument()?.querySelector('meta[name="accountId"]');
	const accountId = meta?.getAttribute('content');

	return accountId || null;
};

const getQueryParam = (key: string) => {
	const value = new URLSearchParams(window.location.search).get(key);

	return value || null;
};

/**
 * Read account ID from the URL query parameter.
 *
 * ⚠️ SECURITY: Query parameters are user-controllable input and must only be used for
 * feature gate targeting and analytics. Never use this value for authentication,
 * authorization, or any other security-sensitive decision.
 */
export const getAccountIdFromQueryParam = (): string | null => {
	return getQueryParam('accountId');
};

/**
 * Read session ID from the URL query parameter.
 *
 * ⚠️ SECURITY: Query parameters are user-controllable input and must only be used for
 * feature gate targeting and analytics. Never use this value for authentication,
 * authorization, or any other security-sensitive decision.
 */
export const getSessionIdFromQueryParam = (): string | null => {
	return getQueryParam('sessionId');
};

/**
 * Generate the anonymous targeting identifier used for unauthenticated users.
 *
 * The identifier format intentionally matches the backend convention so anonymous users
 * can be targeted consistently across SSR, client-side feature gates, and analytics.
 */
export const getAnonymousUserId = (sessionId: string) => {
	return `unidentified-${sessionId}`;
};

/**
 * Retrieve the account ID for feature gate targeting and analytics.
 *
 * ⚠️ SECURITY: This function falls back to query parameters, which are user-controllable.
 * Use it only for targeting and analytics where a manipulated value may change UI treatment
 * but must never grant access or permissions. Do not use this function for authentication
 * or authorization.
 *
 * Resolution order:
 * 1. Trusted accountId meta tag from SSR
 * 2. accountId query parameter for embedded OAuth flows
 * 3. sessionId query parameter for anonymous users, converted to unidentified-${sessionId}
 */
export const getAccountIdForTargeting = (): string | null => {
	const metaAccountId = getAccountIdFromMeta();
	if (metaAccountId) {
		return metaAccountId;
	}

	const queryParamAccountId = getAccountIdFromQueryParam();
	if (queryParamAccountId) {
		return queryParamAccountId;
	}

	const sessionId = getSessionIdFromQueryParam();
	if (sessionId) {
		return getAnonymousUserId(sessionId);
	}

	return null;
};

export const getAccountIdForAnalytics = (): string | null => {
	const metaAccountId = getAccountIdFromMeta();
	if (metaAccountId) {
		return metaAccountId;
	}

	const queryParamAccountId = getAccountIdFromQueryParam();
	if (queryParamAccountId) {
		return queryParamAccountId;
	}

	return null;
};

export const getStatsigValuesFromDOM = (): any => {
	const meta = getDocument()?.querySelector('meta[name="cawi-statsig-values"]');
	if (!meta) {
		return null;
	}

	const value = meta.getAttribute('content');
	if (!value) {
		return null;
	}

	try {
		return JSON.parse(value);
	} catch (error) {
		console.error(error);
		return null;
	}
};
